import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  User, Mail, Phone, Car, CreditCard, Save, X, Loader2, Shield, Star, DollarSign, 
  FileText, MapPin, Camera, CheckCircle, XCircle, AlertCircle, Calendar, Clock,
  Image, Eye, ExternalLink
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const AdminDriverEdit = ({ isOpen, onClose, driverId, backendUrl, onUpdate }) => {
  const [driver, setDriver] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeView, setActiveView] = useState('profile'); // 'profile' or 'edit'
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    paypal_email: '',
    vehicle_type: '',
    vehicle_model: '',
    vehicle_year: '',
    license_plate: '',
    status: '',
    payout_percentage: '',
    rating: '',
    notes: '',
  });

  useEffect(() => {
    if (isOpen && driverId) {
      fetchDriver();
      setActiveView('profile'); // Default to profile view
    }
  }, [isOpen, driverId]);

  const fetchDriver = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/admin/drivers/${driverId}`);
      const driverData = response.data.driver;
      setDriver(driverData);
      setFormData({
        name: driverData.name || '',
        phone: driverData.phone || '',
        email: driverData.email || '',
        paypal_email: driverData.paypal_email || '',
        vehicle_type: driverData.vehicle_type || '',
        vehicle_model: driverData.vehicle_model || '',
        vehicle_year: driverData.vehicle_year || '',
        license_plate: driverData.license_plate || '',
        status: driverData.status || 'pending',
        payout_percentage: driverData.payout_percentage?.toString() || '80',
        rating: driverData.rating?.toString() || '',
        notes: driverData.admin_notes || '',
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load driver data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates = {};
      
      if (formData.name !== driver.name) updates.name = formData.name;
      if (formData.phone !== driver.phone) updates.phone = formData.phone;
      if (formData.email !== driver.email) updates.email = formData.email;
      if (formData.paypal_email !== driver.paypal_email) updates.paypal_email = formData.paypal_email;
      if (formData.vehicle_type !== driver.vehicle_type) updates.vehicle_type = formData.vehicle_type;
      if (formData.vehicle_model !== driver.vehicle_model) updates.vehicle_model = formData.vehicle_model;
      if (formData.vehicle_year !== driver.vehicle_year) updates.vehicle_year = formData.vehicle_year;
      if (formData.license_plate !== driver.license_plate) updates.license_plate = formData.license_plate;
      if (formData.status !== driver.status) updates.status = formData.status;
      if (formData.payout_percentage !== driver.payout_percentage?.toString()) {
        updates.payout_percentage = parseFloat(formData.payout_percentage);
      }
      if (formData.rating && formData.rating !== driver.rating?.toString()) {
        updates.rating = parseFloat(formData.rating);
      }
      if (formData.notes !== driver.admin_notes) updates.notes = formData.notes;

      if (Object.keys(updates).length === 0) {
        toast({ title: "No Changes", description: "No fields were modified" });
        setIsSaving(false);
        return;
      }

      await axios.put(`${backendUrl}/api/admin/drivers/${driverId}`, updates);
      
      toast({ title: "Driver Updated!", description: "Changes have been saved successfully" });
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      toast({
        title: "Update Failed",
        description: error.response?.data?.detail || "Failed to save changes",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuickApprove = async () => {
    try {
      await axios.put(`${backendUrl}/api/admin/drivers/${driverId}`, { status: 'approved' });
      toast({ title: "Driver Approved!", description: `${driver.name} can now accept rides` });
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      toast({ title: "Error", description: "Failed to approve driver", variant: "destructive" });
    }
  };

  const handleQuickReject = async () => {
    if (!window.confirm(`Are you sure you want to reject ${driver?.name}?`)) return;
    try {
      await axios.put(`${backendUrl}/api/admin/drivers/${driverId}`, { status: 'rejected' });
      toast({ title: "Driver Rejected", description: `${driver.name} has been rejected` });
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      toast({ title: "Error", description: "Failed to reject driver", variant: "destructive" });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300',
      suspended: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return styles[status] || styles.pending;
  };

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${backendUrl}${path}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl p-0 relative animate-in fade-in zoom-in-95 max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <div className="flex items-center gap-3">
            <Shield className="text-amber-400" size={24} />
            <div>
              <h2 className="text-lg font-bold">Driver Profile Review</h2>
              <p className="text-xs text-slate-300">Admin access • Full profile details</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={activeView === 'profile' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('profile')}
              className={activeView === 'profile' ? 'bg-amber-500 text-white' : 'text-white hover:bg-slate-700'}
            >
              <Eye size={16} className="mr-1" /> View
            </Button>
            <Button
              variant={activeView === 'edit' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('edit')}
              className={activeView === 'edit' ? 'bg-amber-500 text-white' : 'text-white hover:bg-slate-700'}
            >
              <FileText size={16} className="mr-1" /> Edit
            </Button>
            <button onClick={onClose} className="ml-2 text-slate-300 hover:text-white">
              <X size={20} />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-amber-500" size={40} />
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 p-4">
            {/* Profile View Mode */}
            {activeView === 'profile' && (
              <div className="space-y-4">
                {/* Status Banner for Pending Drivers */}
                {driver?.status === 'pending' && (
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="text-amber-600" size={20} />
                        <span className="font-semibold text-amber-800">Awaiting Approval</span>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleQuickApprove} className="bg-green-600 hover:bg-green-700" size="sm">
                          <CheckCircle size={16} className="mr-1" /> Approve Driver
                        </Button>
                        <Button onClick={handleQuickReject} variant="destructive" size="sm">
                          <XCircle size={16} className="mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Driver Header Card */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-4 border">
                  <div className="flex items-start gap-4 flex-wrap">
                    {/* Profile Photo */}
                    <div className="relative">
                      {driver?.profile_photo || driver?.profile_picture_url ? (
                        <img 
                          src={getPhotoUrl(driver.profile_photo || driver.profile_picture_url)} 
                          alt="Driver" 
                          className="w-24 h-24 rounded-xl object-cover border-4 border-white shadow-lg"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-slate-200 rounded-xl flex items-center justify-center border-4 border-white shadow-lg">
                          <User size={32} className="text-slate-400" />
                        </div>
                      )}
                      <Badge className={`absolute -bottom-2 -right-2 ${getStatusBadge(driver?.status)}`}>
                        {driver?.status}
                      </Badge>
                    </div>

                    {/* Basic Info */}
                    <div className="flex-1 min-w-[200px]">
                      <h3 className="text-2xl font-bold text-slate-800">{driver?.name}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail size={14} /> {driver?.email}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone size={14} /> {driver?.phone}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar size={14} /> Joined: {driver?.join_date ? new Date(driver.join_date).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Clock size={14} /> ID: {driver?.id?.slice(0, 12)}...
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white p-3 rounded-lg text-center shadow-sm">
                        <p className="text-2xl font-bold text-green-600">{driver?.total_rides || 0}</p>
                        <p className="text-xs text-slate-500">Total Rides</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg text-center shadow-sm">
                        <p className="text-2xl font-bold text-amber-600 flex items-center justify-center gap-1">
                          <Star size={16} className="fill-amber-400" /> {driver?.rating?.toFixed(1) || 'N/A'}
                        </p>
                        <p className="text-xs text-slate-500">Rating</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg text-center shadow-sm">
                        <p className="text-2xl font-bold text-blue-600">${(driver?.earnings || 0).toFixed(0)}</p>
                        <p className="text-xs text-slate-500">Earnings</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg text-center shadow-sm">
                        <p className="text-2xl font-bold text-purple-600">{driver?.payout_percentage || 80}%</p>
                        <p className="text-xs text-slate-500">Payout Rate</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vehicle Information */}
                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Car className="text-blue-500" size={18} /> Vehicle Information
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Type</p>
                      <p className="font-medium">{driver?.vehicle_type || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Model</p>
                      <p className="font-medium">{driver?.vehicle_model || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Year</p>
                      <p className="font-medium">{driver?.vehicle_year || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">License Plate</p>
                      <p className="font-medium">{driver?.license_plate || 'Not set'}</p>
                    </div>
                  </div>

                  {/* Vehicle Photos */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase mb-2">Front Photo</p>
                      {driver?.vehicle_front_photo ? (
                        <a href={getPhotoUrl(driver.vehicle_front_photo)} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={getPhotoUrl(driver.vehicle_front_photo)} 
                            alt="Vehicle Front" 
                            className="w-full h-40 object-cover rounded-lg border-2 border-slate-200 hover:border-blue-400 transition-colors cursor-pointer"
                          />
                        </a>
                      ) : (
                        <div className="w-full h-40 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center">
                          <div className="text-center text-slate-400">
                            <Camera size={24} className="mx-auto mb-1" />
                            <p className="text-xs">Not uploaded</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase mb-2">Rear Photo (with plate)</p>
                      {driver?.vehicle_rear_photo ? (
                        <a href={getPhotoUrl(driver.vehicle_rear_photo)} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={getPhotoUrl(driver.vehicle_rear_photo)} 
                            alt="Vehicle Rear" 
                            className="w-full h-40 object-cover rounded-lg border-2 border-slate-200 hover:border-blue-400 transition-colors cursor-pointer"
                          />
                        </a>
                      ) : (
                        <div className="w-full h-40 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center">
                          <div className="text-center text-slate-400">
                            <Camera size={24} className="mx-auto mb-1" />
                            <p className="text-xs">Not uploaded</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Financial & Tax Information */}
                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <DollarSign className="text-green-500" size={18} /> Financial Information
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">PayPal Email</p>
                      <p className="font-medium text-sm break-all">{driver?.paypal_email || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">EIN/SSN</p>
                      <p className="font-medium">
                        {driver?.ein_ssn_masked || driver?.ein_ssn_provided ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle size={14} /> Provided
                          </span>
                        ) : (
                          <span className="text-red-500 flex items-center gap-1">
                            <XCircle size={14} /> Not provided
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Pending Payout</p>
                      <p className="font-medium text-amber-600">${(driver?.pending_payout || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Total Tips</p>
                      <p className="font-medium text-green-600">${(driver?.total_tips || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Service Types */}
                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <MapPin className="text-purple-500" size={18} /> Service Types
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {driver?.service_types?.length > 0 ? (
                      driver.service_types.map((type, idx) => (
                        <Badge key={idx} variant="outline" className="capitalize">
                          {type === 'transportation' ? '🚗 Transportation' : '📦 Delivery'}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-slate-400 text-sm">No services selected</span>
                    )}
                  </div>
                </div>

                {/* Admin Notes */}
                {driver?.admin_notes && (
                  <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
                    <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                      <FileText className="text-purple-500" size={18} /> Admin Notes
                    </h4>
                    <p className="text-sm text-purple-700 whitespace-pre-wrap">{driver.admin_notes}</p>
                  </div>
                )}

                {/* Quick Actions for Approved Drivers */}
                {driver?.status === 'approved' && (
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setActiveView('edit')}>
                      Edit Profile
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Edit Mode */}
            {activeView === 'edit' && (
              <div className="space-y-4">
                {/* Status & Admin Controls */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                  <div>
                    <Label className="flex items-center gap-2 text-purple-700 mb-1.5 font-semibold">
                      <Shield size={14} /> Status
                    </Label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-md border-2 border-purple-300 focus:border-purple-500 bg-white"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 text-purple-700 mb-1.5 font-semibold">
                      <DollarSign size={14} /> Payout %
                    </Label>
                    <Input
                      name="payout_percentage"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.payout_percentage}
                      onChange={handleChange}
                      className="border-2 border-purple-300 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 text-purple-700 mb-1.5 font-semibold">
                      <Star size={14} /> Rating
                    </Label>
                    <Input
                      name="rating"
                      type="number"
                      min="0"
                      max="5"
                      step="0.1"
                      value={formData.rating}
                      onChange={handleChange}
                      placeholder="4.9"
                      className="border-2 border-purple-300 focus:border-purple-500"
                    />
                  </div>
                </div>

                {/* Personal Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 text-gray-700 mb-1.5">
                      <User size={14} /> Full Name
                    </Label>
                    <Input name="name" value={formData.name} onChange={handleChange} className="border-2" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 text-gray-700 mb-1.5">
                      <Phone size={14} /> Phone
                    </Label>
                    <Input name="phone" value={formData.phone} onChange={handleChange} className="border-2" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 text-gray-700 mb-1.5">
                      <Mail size={14} /> Email
                    </Label>
                    <Input name="email" type="email" value={formData.email} onChange={handleChange} className="border-2" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 text-gray-700 mb-1.5">
                      <CreditCard size={14} /> PayPal Email
                    </Label>
                    <Input name="paypal_email" type="email" value={formData.paypal_email} onChange={handleChange} className="border-2" />
                  </div>
                </div>

                {/* Vehicle Info */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Car size={18} /> Vehicle Information
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-gray-700 mb-1.5">Type</Label>
                      <Input name="vehicle_type" value={formData.vehicle_type} onChange={handleChange} className="border-2" />
                    </div>
                    <div>
                      <Label className="text-gray-700 mb-1.5">Model</Label>
                      <Input name="vehicle_model" value={formData.vehicle_model} onChange={handleChange} className="border-2" />
                    </div>
                    <div>
                      <Label className="text-gray-700 mb-1.5">Year</Label>
                      <Input name="vehicle_year" value={formData.vehicle_year} onChange={handleChange} className="border-2" />
                    </div>
                    <div>
                      <Label className="text-gray-700 mb-1.5">Plate</Label>
                      <Input name="license_plate" value={formData.license_plate} onChange={handleChange} className="border-2" />
                    </div>
                  </div>
                </div>

                {/* Admin Notes */}
                <div>
                  <Label className="flex items-center gap-2 text-gray-700 mb-1.5">
                    <FileText size={14} /> Admin Notes
                  </Label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Internal notes about this driver..."
                    className="w-full px-3 py-2 rounded-md border-2 border-gray-200 focus:border-purple-500 focus:outline-none resize-none"
                  />
                </div>

                {/* Save Button */}
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setActiveView('profile')} className="flex-1">
                    Back to Profile
                  </Button>
                  <Button 
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
                  >
                    {isSaving ? (
                      <><Loader2 className="animate-spin mr-2" size={18} />Saving...</>
                    ) : (
                      <><Save className="mr-2" size={18} />Save Changes</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminDriverEdit;
